'use no memo';

import {isFeatureEnabled} from 'feature-flags';
import {Switch} from 'platform/components';
import {DeepReadonly} from 'utility-types';

import {ReactElement, useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useDisablePlatformForVehicleMutation,
  useEnablePlatformForVehicleMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {handleApiError, usePkwEnvkvAdvertisingPreflight} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';

type SwitcherProps = {
  code: AdvertisingPlatformCodeEnum;
  value?: boolean;
  isDisabled: boolean;
  // Nullish until the general settings have loaded.
  inSale: boolean | Nullish;
};

export const PlatformSwitcher = ({
  value: initialValue,
  code,
  isDisabled,
  inSale,
}: DeepReadonly<SwitcherProps>): ReactElement => {
  const [value, setValue] = useState<boolean>(!!initialValue);
  const {id: vehicleId} = useParams();
  const {resetPollingInterval} = useAdvertisementPolling();
  const {runPkwEnvkvPreflight, isChecking} = usePkwEnvkvAdvertisingPreflight();
  const [enablePlatform, {isLoading: isEnabling}] = useEnablePlatformForVehicleMutation();
  const [disablePlatform, {isLoading: isDisabling}] = useDisablePlatformForVehicleMutation();

  useEffect(
    function syncValueFromInitialValue() {
      if (!isNil(initialValue)) {
        setValue(initialValue);
      }
    },
    [initialValue]
  );

  const onChange = useCallback(
    (newValue: boolean) => {
      if (isNil(vehicleId)) {
        return;
      }

      // A failed request must not leave the optimistic switch state behind.
      const revertOnFailure = (error: ApiError) => {
        setValue(!newValue);
        handleApiError(error);
      };

      if (!newValue) {
        // Disable never goes through the EnVKV preflight.
        setValue(false);
        disablePlatform({vehicleId, platformCode: code})
          .unwrap()
          // The accepted request changes the platform state, so the polling restarts now.
          .then(resetPollingInterval)
          .catch(revertOnFailure);
        return;
      }

      if (isNil(inSale)) {
        // The sale state is not loaded yet; unknown is not permission to publish.
        return;
      }

      const enable = () => {
        resetPollingInterval();
        setValue(true);
        enablePlatform({
          vehicleId,
          platformCode: code,
          publishMobileDeViaAdvertisingBridge:
            code === AdvertisingPlatformCodeEnum.MOBILE_DE &&
            isFeatureEnabled(featureFlags.ADVERTISEMENT_MOBILE_DE_PROMPT_BRIDGE),
          publishVgpViaAdvertisingBridge:
            [AdvertisingPlatformCodeEnum.VGP, AdvertisingPlatformCodeEnum.AUDI_PL].includes(code) &&
            isFeatureEnabled(featureFlags.ADVERTISEMENT_VGP_PROMPT_BRIDGE),
          publishOtomotoViaAdvertisingBridge:
            code === AdvertisingPlatformCodeEnum.OTOMOTO &&
            isFeatureEnabled(featureFlags.ADVERTISEMENT_OTOMOTO_PROMPT_BRIDGE),
          publishAutocarisViaAdvertisingBridge:
            code === AdvertisingPlatformCodeEnum.AUTOCARIS &&
            isFeatureEnabled(featureFlags.ADVERTISEMENT_AUTOCARIS_PROMPT_BRIDGE),
          publishMercedesNaSkladeViaAdvertisingBridge:
            code === AdvertisingPlatformCodeEnum.MERCEDES_NA_SKLADE_REST &&
            isFeatureEnabled(featureFlags.ADVERTISEMENT_MERCEDES_NA_SKLADE_PROMPT_BRIDGE),
          publishHyundaiPromiseViaAdvertisingBridge:
            code === AdvertisingPlatformCodeEnum.HYUNDAI_PROMISE &&
            isFeatureEnabled(featureFlags.ADVERTISEMENT_HYUNDAI_PROMISE_PROMPT_BRIDGE),
        })
          .unwrap()
          .catch(revertOnFailure);
      };

      if (!inSale) {
        // Outside a sale the switch only presets the platform and publishes
        // nothing, so the EnVKV check must not block it.
        enable();
        return;
      }

      // For a vehicle in sale, enabling publishes right away, so the fresh
      // EnVKV check runs before any optimistic state change; a RED vehicle
      // keeps the switch off.
      void runPkwEnvkvPreflight(vehicleId, enable);
    },
    [
      resetPollingInterval,
      enablePlatform,
      disablePlatform,
      vehicleId,
      code,
      inSale,
      runPkwEnvkvPreflight,
    ]
  );

  return (
    <Switch
      value={value}
      onChange={onChange}
      name={`advertisement-platform-switcher-${code}`}
      // Publishing statuses of the platform must not block the switch, only missing permissions do.
      isDisabled={isDisabled}
      isLoading={isEnabling || isDisabling || isChecking}
      data-testid={`switch-advertisement-platforms-switcher-${code}`}
    />
  );
};
